import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Divider,
  Text,
} from "@fluentui/react-components";
import {
  CalendarClock20Regular,
  Dismiss24Regular,
  DocumentRegular,
  InfoRegular,
} from "@fluentui/react-icons";
import { devices } from "library/constants";
import { fileExtensionIcons } from "library/shared";
import { EApprovalStatus, ICardBenefitPackage } from "library/types";
import { formatDateString, getExtensionFromUrl, openLinkInNewTab } from "library/utils";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";

import { ReactComponent as MoneyIcon } from "assets/icons/moneyIcon.svg";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  benefitPackage?: ICardBenefitPackage;
  formattedPrice?: string;
  benefitStatusConfig: {
    text: string;
    color: string;
    background: string | undefined;
  };
  eligible?: boolean;
};

export function BenefitDetailsDialog({
  open,
  setOpen,
  benefitPackage,
  formattedPrice,
  benefitStatusConfig,
  eligible,
}: Props) {
  const { t } = useTranslation();
  const documents = benefitPackage?.enrollmentDocuments || [];


  const packageValidFrom = formatDateString(benefitPackage?.validFrom || "");
  const packageValidTo = formatDateString(benefitPackage?.validTo || "");

  const dateOfEnrollment = formatDateString(benefitPackage?.dateOfEnrollment || "");

  const eligibleDates = `${formatDateString(
    benefitPackage?.dateOfEligibility || ""
  )} - ${formatDateString(benefitPackage?.endDateOfEligibility || "")}`;

  const cancellationPossibility = formatDateString(benefitPackage?.cancellationPossibility || "");

  const rejectedStatusConfig = (() => {
    const output = {
      hasRejectedStatus: false,
      rejectionComment: "",
      action: "",
    };
    if (benefitPackage?.approveEnrollStatusCode === EApprovalStatus.REJECTED) {
      return {
        hasRejectedStatus: true,
        action: `${t("hbh.rejected.label")} ${t("hbh.enroll.label")}`,
        rejectionComment: benefitPackage?.rejectionComment || "",
      };
    }
    if (benefitPackage?.cancelEnrollApprovalStatusCode === EApprovalStatus.REJECTED) {
      return {
        hasRejectedStatus: true,
        action: `${t("hbh.rejected.label")} ${t("hbh.cancelEnroll.label")}`,
        rejectionComment: benefitPackage?.rejectionComment || "",
      };
    }
    return output;
  })();

  return (
    <Dialog open={open} onOpenChange={(event, data) => setOpen(data.open)}>
      <StyledDialogSurface
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogBody>
          <DialogTitle
            style={{
              marginBottom: 20,
            }}
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            <Text
              block
              align="start"
              weight="semibold"
              size={400}
              style={{
                color: benefitStatusConfig.color,
                background: benefitStatusConfig.background,
                padding: "5px 10px",
                borderRadius: "5px",
                width: "fit-content",
              }}
            >
              {benefitStatusConfig.text}
            </Text>
          </DialogTitle>

          <DialogContent>
            <Flex $justify="space-between" align="center">
              <InfoPill>
                <CalendarClock20Regular />
                {`${packageValidFrom} - ${packageValidTo}`}
              </InfoPill>
              <InfoPill>
                <StyledMoneyIcon />
                {formattedPrice}
              </InfoPill>
            </Flex>
            {rejectedStatusConfig.hasRejectedStatus && (
              <RejectedStatusContainer>
                <InfoRegular fontSize={20} />
                <Text weight="bold">{rejectedStatusConfig.action}: </Text>
                <Text weight="semibold">{rejectedStatusConfig.rejectionComment}</Text>
              </RejectedStatusContainer>
            )}
            <Name>{benefitPackage?.name}</Name>
            <Text>{benefitPackage?.description}</Text>
            <Divider
              style={{
                margin: "20px 0",
              }}
            />
            <Flex $justify="space-between" align="center">
              <Flex align="center">
                {eligible ? (
                  <>
                    <LabelWrap>{t("hbh.eligibleFromTo.label")}:</LabelWrap>
                    <Text>{eligibleDates}</Text>
                  </>
                ) : (
                  <>
                    <LabelWrap>{t("hbh.enrolledOn.label")}:</LabelWrap>
                    <Text>{dateOfEnrollment}</Text>
                  </>
                )}
              </Flex>
              <Flex>
                <LabelWrap>{t("hbh.cancellationPossibility.label")}:</LabelWrap>
                <Text>{cancellationPossibility}</Text>
              </Flex>
            </Flex>
            <DocumentList>
              {documents?.map((doc) => {
                const docType = getExtensionFromUrl(doc.attachmentPath);
                // const isDocumentFromPackage = doc.attachmentObjectTag?.toString() === benefitPackage?.benefitPackageTag?.toString()
                // if(!isDocumentFromPackage) return null;
                return (
                  <DocItemButton
                    key={doc.attachmentName}
                    onClick={() => {
                      openLinkInNewTab(doc.attachmentPath || "");
                    }}
                    icon={fileExtensionIcons[docType] || <DocumentRegular />}
                    appearance="subtle"
                  >
                    <Text key={doc.attachmentName} weight="semibold">
                      {doc.attachmentName}
                    </Text>
                  </DocItemButton>
                );
              })}
            </DocumentList>
          </DialogContent>
        </DialogBody>
      </StyledDialogSurface>
    </Dialog>
  );
}

const StyledDialogSurface = styled(DialogSurface)`
  margin-left: 10px;
  margin-right: 10px;
  width: 95%;
  min-width: 95%;
  max-height: 98vh;
  padding: 15px;

  @media only screen and ${devices.md} {
    width: 800px;
    min-width: 800px;
    padding: 24px;

    margin-left: auto;
    margin-right: auto;
  }
`;

const DocItemButton = styled(Button)`
  display: flex;
  justify-content: flex-start !important;
  text-align: left;
  color: ${(props) => props.theme.palette.purpleDark};
  text-decoration: underline;
  padding-left: 0px;
  width: 100%;
`;

const InfoPill = styled.div`
  display: flex;
  background: ${(props) => props.theme.palette.purpleDark};
  align-items: center;
  color: ${(props) => props.theme.palette.white};
  padding: 5px 10px;
  gap: 5px;
  border-radius: 3px;
`;

const Name = styled(Text)`
  display: flex !important;
  font-weight: 600;
  font-size: 20px;
  color: ${(props) => props.theme.palette.purpleDark};
  padding: 20px 0px 10px;
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0;
`;
const LabelWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${(props) => props.theme.semanticColors.disabledText};
`;

const RejectedStatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 20px 0 0px;
  padding: 10px 20px;
  background: ${(props) => props.theme.palette.orangeLighter};
  color: ${(props) => props.theme.palette.orange};
`;

export const StyledMoneyIcon = styled(MoneyIcon)`
  path {
    fill: ${({ theme }) => theme.palette.white};
  }
`;
